/**
 * Helper: Cart functions
 * -----------------------------------------------------------------------------
 * Global configuration for adding and removing from card.
 * Fire's event bus to trigger all other functions.
 */
import * as cart from '@shopify/theme-cart';

import cssClasses from './cssClasses';
import {on} from './utils';

/**
 * DOM selectors.
 */
const selectors = {
  container: '[js-cart="container"]',
  addToCart: '[js-cart="addToCart"]',
  remove: '[js-cart="remove"]',
  error: '[js-cart="error"]',
};

/**
 * Text Strings.
 */
const strings = {
  buttonDefault: 'Add to bag',
  buttonLoading: 'Adding...',
  addToCartError: 'Looks like something went wrong',
};

/**
 * Export default cartFunctions.
 */
export default () => {
  // DOM node selectors.
  const nodeSelectors = {
    addToCart: [...document.querySelectorAll(selectors.addToCart)],
    container: [...document.querySelectorAll(selectors.container)],
  };

  const variants = [];
  const swatchTitle = document.querySelector(
    '[js-product-swatches="swatchTitleContainer"]',
  );

  Karmoon.EventBus.listen('AddToCart:variantSelected', (selectedVariant) => {
    variants.push(selectedVariant);
  });

  /**
   * Handle add to cart event.
   * @param {Object} event - Submited event.
   * @param {HTMLElement} form - Submited form.
   */
  function handleAddToCart(event, form) {
    event.preventDefault();

    const label = document.querySelector('.karmoon-option-select');
    // console.log(selectLabel.classList);

    let variant;

    variants.forEach((item) => {
      if (form.contains(item.selector)) {
        variant = item;
      }
    });

    if (label) {
      const selectLabel = label.querySelector('.karmoon--btn-select');
      if (selectLabel.classList.contains('not-value')) {
        console.log('NOTHING SELECTED!');
        document
          .querySelector('.karmoon-option-select')
          .classList.add('highlighted');
        if (swatchTitle) {
          swatchTitle.innerHTML = 'Please select shade:';
        }
        return;
      }
    }

    if (variant && !variant.state) {
      variant.selector.classList.add('highlighted');
      ifInViewport(variant.selector);

      if (swatchTitle) {
        swatchTitle.innerHTML = 'Please select shade:';
      }
      return;
    }

    if (variant && variant.state) {
      variant.selector.classList.remove('highlighted');
    }

    setLoadingState(form);
    clearErrorState(form);

    cart
      .addItemFromForm(form)
      .then((item) => {
        Karmoon.EventBus.emit('AddToCart:itemAdded', item);
        setDefaultState(form);
      })
      .catch((error) => {
        Karmoon.EventBus.emit('AddToCart:error', form, error);
        setDefaultState(form);
        setErrorState(form);
        return error;
      });
  }

  /**
   * If variant select is in viewport.
   * @param {HTMLElement} element - variant select element.
   */
  function ifInViewport(element) {
    if (element.getBoundingClientRect().top < 160) {
      window.scrollTo(0, element.offsetTop - 200);
    }
  }

  /**
   * Handle update quantity from cart event.
   * @param {Object} event - Submit event.
   */
  function handleRemoveFromCart(event) {
    event.preventDefault();
    const item = event.target;
    const key = item.dataset.itemKey;

    console.log('Remove Item');

    Karmoon.EventBus.emit('AddToCart:isRemoving');

    cart.removeItem(key).then(() => {
      Karmoon.EventBus.emit('AddToCart:itemRemoved');
    });
  }

  /**
   * Handle add to cart loading state.
   * @param {HTMLElement} form - Submited form.
   */
  function setLoadingState(form) {
    const addToCartButton = form.querySelector(selectors.addToCart);
    addToCartButton.innerHTML = strings.buttonLoading;
  }

  /**
   * Clear add to cart error state.
   * @param {HTMLElement} form - Submited form.
   */
  function clearErrorState(form) {
    const addToCartError = form.querySelector(selectors.error);
    addToCartError.innerHTML = '';
    addToCartError.classList.remove(cssClasses.active);
  }

  /**
   * Set Error on Form.
   * @param {HTMLElement} form - Submited form.
   */
  function setErrorState(form) {
    const addToCartError = form.querySelector(selectors.error);
    addToCartError.innerHTML = strings.addToCartError;
    addToCartError.classList.add(cssClasses.active);
  }

  /**
   * Handle add to cart default state.
   * @param {HTMLElement} form - Submited form.
   */
  function setDefaultState(form) {
    const addToCartButton = form.querySelector(selectors.addToCart);
    addToCartButton.innerHTML = strings.buttonDefault;
  }

  /**
   * Set Event Listeners.
   */
  function setEventListeners() {
    nodeSelectors.container.forEach((form) => {
      on('submit', form, (event) => handleAddToCart(event, form));
    });

    document.addEventListener('click', (event) => {
      // All click events will be handled by this function, so it needs to be as cheap as possible. To check
      // whether this function should be invoked, we're going to check whether the element that was clicked on
      // was the elemnt that we care about. The element that was clicked on is made available at "e.target"
      const element = event.target;

      // Check if it matches our previously defined selector
      if (element.matches(selectors.remove)) {
        handleRemoveFromCart(event);
      }
    });
  }

  function init() {
    setEventListeners();
  }

  /**
   * Return immutable object.
   */
  return Object.freeze({
    init,
  });
};
